/**
 * 🎨 МАСТЕР-КОНФИГ ДИЗАЙНА (ПРОКСИ)
 * 
 * Прокси на новую архитектуру дизайн-токенов для обратной совместимости.
 * НЕ РЕДАКТИРУЙТЕ ЗНАЧЕНИЯ ЗДЕСЬ - они теперь в src/shared/design/
 * 
 * UI токены: src/shared/design/ui-tokens.ts
 * Цвета: через CSS vars из цветовой системы (src/shared/design/colors/)
 * 
 * ============================================
 * 📋 UI KIT КОМПОНЕНТЫ И ИХ ВАРИАНТЫ
 * ============================================
 * 
 * 🔘 КНОПКИ (Button):
 *   - primary — с заливкой primary (var(--primary-fill))
 *   - secondary — белая заливка (var(--color-card)) + серая обводка (var(--color-border))
 *   - ghost — без заливки, без обводки (прозрачный)
 *   - ghost + danger prop — без заливки, красный текст (var(--color-danger))
 *   - danger — красная заливка (var(--color-error))
 *   Размеры:
 *   - sm — высота 40px
 *   - md — высота 48px
 * 
 * 🔘 КНОПКА С ИКОНКОЙ (IconButton):
 *   - primary — с заливкой primary-light (var(--color-primary-light))
 *   - secondary — с серой обводкой (var(--color-border))
 *   - ghost — без заливки, без обводки (прозрачный)
 *   - ghost + danger prop — без заливки, красная иконка (var(--color-danger))
 *   Размеры:
 *   - sm — 32px × 32px
 *   - md — 40px × 40px
 *   - lg — 48px × 48px
 * 
 * 🔀 ПЕРЕКЛЮЧАТЕЛЬ (SegmentedToggle):
 *   - default — обычный вид (активная пилюля белая var(--segmented-pill-bg-active))
 *   - inverted — активная пилюля белая/поверхность (var(--color-surface))
 *   - status — активная пилюля по цвету статуса (var(--appointment-bg-header-*))
 *   Размеры:
 *   - sm — высота 28px, padding-x 8px, radius 10px
 *   - md — высота 36px, padding-x 12px, radius 10px
 *   - lg — высота 48px, padding-x 16px, radius 10px
 * 
 * 📝 ТЕКСТ (Text):
 *   Варианты:
 *   - title — заголовок (lg, bold)
 *   - section — секция (md, semibold)
 *   - body — основной текст (sm, normal)
 *   - meta — мета-информация (xs, medium)
 *   - caption — подпись (xs, normal)
 *   - button — текст кнопки (sm, medium)
 *   Тоны:
 *   - default — основной цвет (var(--color-text))
 *   - muted — приглушенный (var(--color-text-muted))
 *   - danger — красный (var(--color-danger))
 *   - success — зеленый (var(--color-success))
 *   - warning — желтый (var(--color-warning))
 * 
 * 📥 ПОЛЕ ВВОДА (Input):
 *   Размер: один размер — высота 48px (без size API)
 * 
 * 📋 ВЫПАДАЮЩИЙ СПИСОК (Select):
 *   Размер: один размер — высота 48px (без size API)
 * 
 * ✅ ЧЕКБОКС (Checkbox):
 *   Размер: 16px × 16px (единый размер для всех вариантов)
 * 
 * 🏷️ БЕЙДЖ СТАТУСА (AppointmentStatusBadge):
 *   Режимы: compact, stripeOnly
 *   Цвета берутся из appointment.bgHeader* (единый источник правды)
 * 
 * 🪟 МОДАЛКИ (Modal / ModalShell / FormModalLayout):
 *   - Скролл только на .modal-overlay — целиком лист (шапка + тело + футер).
 *   - Внутреннего скролла в .modalBody нет. Исключение: календарь (.calendar-modal-fullscreen, index.css).
 *   - Закрытие в шапке: только стрелка «назад», без крестика.
 *   - SSOT правил: src/shared/ui/Modal.css (шапка), Modal.tsx.
 * 
 * ============================================
 */

// Импорт новой архитектуры
import { uiTokens } from '../design/ui-tokens';
import { v2Tokens } from '../design/v2-tokens';

export const designConfig = {
  // ============================================
  // 🎨 ОСНОВНЫЕ ЦВЕТА ФОНА И ПОВЕРХНОСТЕЙ
  // ============================================
  
  colors: {
    // Фон приложения (самый дальний слой)
    background: 'var(--c-bg)',
    
    // Поверхность контента (средний слой)
    surface: 'var(--c-surface)',
    
    // Карточки, модалки, поля ввода (ближний слой)
    card: 'var(--c-card)',
    
    // Границы элементов
    border: 'var(--c-border)',
    
    // Сильная граница (для выделения)
    borderStrong: 'var(--c-border-strong)',
    
    // Темный фон (для специальных случаев) - оставляем как есть для обратной совместимости
    bgDark: '#15130C',
  },

  // ============================================
  // 📝 ЦВЕТА ТЕКСТА
  // ============================================
  
  text: {
    // Основной текст
    primary: 'var(--c-text)',
    
    // Второстепенный текст (подписи, мета-информация)
    muted: 'var(--c-text-muted)',
    
    // Третичный текст (плейсхолдеры, подсказки)
    tertiary: '#8B9098', // Оставляем для обратной совместимости
    
    // Плейсхолдер в полях ввода
    placeholder: '#9AA3B2', // Оставляем для обратной совместимости
    
    // Отключенный текст
    disabled: 'var(--c-disabled)',
  },

  // ============================================
  // 🟡 PRIMARY (основной акцентный цвет)
  // ============================================
  
  primary: {
    // Основной цвет кнопок, выделений
    main: 'var(--c-primary)',

    // Светлый вариант (для hover, selected) - через CSS переменные из тем
    light: 'var(--c-primary-light)',

    // Альтернативный (для outline кнопок) - через CSS переменные из тем
    alt: 'var(--c-primary)',

    // Цвет текста на primary фоне
    onPrimary: 'var(--c-on-primary)',

    // Цвет текста на primary-light фоне - через CSS переменные из тем
    onPrimaryContainer: 'var(--c-on-primary-container)',

    // Эффект нажатия - теперь адаптируется под тему
    pressEffect: 'rgba(from var(--c-primary) r g b / 0.35)',

    // Кольцо фокуса (focus ring)
    focusRing: 'var(--c-focus)',
  },

  // ============================================
  // ✅ SUCCESS (успех, завершено)
  // ============================================
  
  success: {
    // Основной цвет
    main: 'var(--c-success)',
    
    // Светлый фон
    light: '#D9F1E4', // Оставляем для обратной совместимости
    
    // Темный вариант
    dark: '#2F5E44', // Оставляем для обратной совместимости
    
    // Текст на success фоне
    onSuccess: 'var(--c-on-success)',
  },

  // ============================================
  // ⚠️ WARNING (предупреждение)
  // ============================================
  
  warning: {
    // Основной цвет
    main: 'var(--c-warning)',
    
    // Светлый фон
    light: '#F7E7CF', // Оставляем для обратной совместимости
    
    // Темный вариант
    dark: '#7A5220', // Оставляем для обратной совместимости
    
    // Текст на warning фоне
    onWarning: 'var(--c-on-warning)',
  },

  // ============================================
  // ❌ ERROR / DANGER (ошибка, опасность)
  // ============================================
  
  error: {
    // Основной цвет
    main: 'var(--c-error)',
    
    // Светлый фон
    light: '#FCE1E1', // Оставляем для обратной совместимости
    
    // Темный вариант
    dark: '#7F2E2E', // Оставляем для обратной совместимости
    
    // Текст на error фоне
    onError: 'var(--c-on-error)',
  },

  // ============================================
  // ℹ️ INFO (информация)
  // ============================================
  
  info: {
    // Основной цвет
    main: 'var(--c-info)',
    
    // Светлый фон
    light: '#E1EEF8', // Оставляем для обратной совместимости
    
    // Темный вариант
    dark: '#2E5877', // Оставляем для обратной совместимости
    
    // Текст на info фоне
    onInfo: 'var(--c-on-info)',
  },

  // ============================================
  // 🔘 MUTED (нейтральный, серый)
  // ============================================
  
  muted: {
    // Основной цвет
    main: '#8B9098', // Оставляем для обратной совместимости
    
    // Светлый фон
    light: '#EEF1F4', // Оставляем для обратной совместимости
    
    // Темный вариант
    dark: '#5B616A', // Оставляем для обратной совместимости
    
    // Текст на muted фоне
    onMuted: '#2A2F36', // Оставляем для обратной совместимости
  },

  // ============================================
  // 🏷️ BADGE (бейджи, индикаторы)
  // ============================================
  
  badge: {
    // Фон для compact бейджа (белый полупрозрачный 70%)
    compactBg: 'rgba(255, 255, 255, 0.7)',
  },

  // ============================================
  // 🌑 OVERLAY (затемнение, модалки)
  // ============================================
  
  overlay: {
    // Стандартное затемнение (модалки)
    default: 'var(--c-scrim)',
    
    // Светлое затемнение (fullscreen loading)
    light: 'rgba(0, 0, 0, 0.2)', // Оставляем для обратной совместимости
    
    // Темное затемнение (важные модалки)
    dark: 'rgba(0, 0, 0, 0.6)', // Оставляем для обратной совместимости
  },

  // ============================================
  // 📅 ЦВЕТА ЗАПИСЕЙ (APPOINTMENT)
  // ============================================
  
  appointment: {
    // Фон тела карточки записи (использует colors.card)
    bgBody: '#FFFFFF', // TODO: заменить на colors.card для единого источника правды
    
    // ============================================
    // 🎨 ЕДИНЫЙ ИСТОЧНИК ПРАВДЫ: ЦВЕТА СТАТУСОВ
    // ============================================
    // Цвета статусов (используются везде: бейджи, переключатели, календарь и т.д.)
    bgHeaderScheduled: 'var(--c-appt-scheduled-bg)',    // Запланировано (желтый)
    bgHeaderCompleted: 'var(--c-appt-completed-bg)',    // Завершено (зеленый)
    bgHeaderNoShow: 'var(--c-appt-no-show-bg)',         // Не пришел (бежевый)
    bgHeaderCancelled: 'var(--c-appt-cancelled-bg)',    // Отменено (красный)
    bgHeaderMoved: 'var(--c-appt-moved-bg)',            // Перенесено (фиолетовый)
    bgHeaderBreak: 'var(--c-appt-break-bg)',            // Перерыв (серый)
    
    // Обводка карточки
    border: 'none', // или '1px solid #4E8A67' если нужна обводка
    
    // Тень карточки
    shadow: '0 2px 2px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
    
    // Скругление углов
    radius: '8px',
    
    // Отступы внутри карточки
    paddingX: '12px',
    paddingY: '8px',
  },

  // ============================================
  // 🎛️ CONTROL WELL (фон для контролов)
  // ============================================
  
  controlWell: {
    // Фон для групп контролов
    bg: '#E6EAF0',
    
    // Граница
    border: '#D5DAE3',
  },

  // ============================================
  // 🎯 АКЦЕНТЫ КОМПОНЕНТОВ (точечные UI-цвета)
  // ============================================
  uiAccents: {
    bottomTab: {
      activeFg: 'var(--color-primary)',
    },
    tones: {
      blue: { fg: '#3B82F6', surface: '#DBEAFE' },
      green: { fg: '#22C55E', surface: '#DCFCE7' },
      yellow: { fg: '#FFC107', surface: '#FEF3C7' },
      purple: { fg: '#8B5CF6', surface: '#EDE9FE' },
      pink: { fg: '#E65C96', surface: '#FDE7F1' },
      teal: { fg: '#14B8A6', surface: '#CCFBF1' },
    },
  },

  // ============================================
  // 📏 ОТСТУПЫ (SPACING)
  // ============================================
  
  spacing: uiTokens.spacing,

  /** Семантические алиасы (UI-KIT). Ссылки на существующие значения, без новых чисел. */
  spacingAliases: {
    headerToContent: uiTokens.spacing[8],
    sectionGap: uiTokens.spacing[16],
    blockGap: uiTokens.spacing[8],
    fieldGap: uiTokens.spacing[8],
    labelToField: uiTokens.spacing[8],
  },

  // ============================================
  // 🔲 СКРУГЛЕНИЕ УГЛОВ (RADIUS)
  // ============================================
  
  radius: uiTokens.radius,

  // ============================================
  // 📐 ТЕНИ (ELEVATION)
  // ============================================
  
  elevation: uiTokens.elevation,

  // ============================================
  // 📚 Z-INDEX (слои)
  // ============================================
  
  zIndex: uiTokens.zIndex,

  // ============================================
  // 🪟 МОДАЛКИ (MODAL)
  // ============================================
  
  modal: uiTokens.modal,

  // ============================================
  // 🎬 ЭФФЕКТЫ (EFFECTS)
  // ============================================
  
  effects: uiTokens.effects,

  // ============================================
  // 📋 СПИСКИ (LISTS)
  // ============================================
  
  list: uiTokens.list,

  // ============================================
  // 📏 РАЗМЕРЫ КОНТРОЛОВ (CONTROL SIZES)
  // ============================================
  
  control: uiTokens.control,

  // ============================================
  // 🔀 ПЕРЕКЛЮЧАТЕЛИ (SEGMENTED CONTROL)
  // ============================================
  
  segmented: {
    // Размеры для SegmentedToggle (из uiTokens)
    ...uiTokens.segmented,

    // Фон контейнера (подложка) - теперь через CSS vars для поддержки тем
    containerBg: 'var(--color-surface)',
    
    // Цвета активных элементов для тематических статусов берутся из appointment.bgHeader*
    // (единый источник правды - см. appointment.bgHeader*)
    // Для обычных переключателей (не тематических) используется activeDefault
    
    // Дефолтный цвет для активного элемента (чистый белый)
    activeDefault: '#FFFFFF',
    
    // Цвета
    pillBg: 'transparent', // Фон неактивного сегмента
    pillBgActive: '#FFFFFF', // Фон активного сегмента (default variant)
    text: 'var(--c-text-muted)', // Текст неактивного сегмента
    textActive: 'var(--c-text)', // Текст активного сегмента (default variant)
  },

  // ============================================
  // 📐 РАЗМЕРЫ ЛАЙАУТА (LAYOUT DIMENSIONS)
  // ============================================
  
  layout: uiTokens.layout,

  // ============================================
  // 📱 ЭКРАНЫ (SCREEN LAYOUT)
  // ============================================
  
  screen: {
    ...uiTokens.screen,
    // Цвета берутся из цветовой системы
    bg: 'var(--c-bg)',
    surface: 'var(--c-surface)',
  },

  // ============================================
  // 🔤 ШРИФТ (TYPOGRAPHY)
  // ============================================
  
  typography: uiTokens.typography,

  // ============================================
  // 🎨 V2 REDESIGN TOKENS (Ink) — extension only
  // ============================================

  v2: v2Tokens,
} as const;
